import {
  PrimaryControlsStripTitleContext,
  type PrimaryControlsStripTitleRegistry,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.title";
import type { Tagged } from "@-/com.npmjs.type-fest";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { useContext, useSyncExternalStore } from "react";

export type PrimaryControlsStripTitleHostGate = Tagged<
  { registry: PrimaryControlsStripTitleRegistry },
  "PrimaryControlsStripTitleHostGate"
>;

/**
 * Whether a header title shows: some focused screen published one. `null` is the designed hidden
 * state. The gate carries only the registry handle and flips only when a title appears or
 * disappears; the host subscribes to the title content itself, so a title change re-renders only
 * the host, never the header around it.
 *
 * `redundantTitle` hides a title that only repeats it: the web header already names the section
 * ("Settings"), so a breadcrumb reading "Settings" again would say nothing new.
 */
export function usePrimaryControlsStripTitleHostGate({
  redundantTitle,
}: { redundantTitle?: string } = {}): PrimaryControlsStripTitleHostGate | null {
  const registry = useContext(PrimaryControlsStripTitleContext);
  const getHasTitle = () => {
    const title = registry.getCurrent();
    return title != null && (redundantTitle === undefined || title !== redundantTitle);
  };
  const hasTitle = useSyncExternalStore(registry.subscribe, getHasTitle, getHasTitle);
  return hasTitle ? createGate<PrimaryControlsStripTitleHostGate>({ registry }) : null;
}
